import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
	Button,
	Modal,
	Form,
	Input,
	Select,
	Table,
	Pagination,
	message,
	notification,
	Tooltip,
	Popconfirm,
	Space,
	Tabs,
	InputNumber,
	Slider,
	Radio,
	Card,
	Typography,
	Divider
} from "antd";
import { useTranslation } from "react-i18next";
import { fetchWrapper } from "../../helpers";
import { SETTINGS } from "../../constants/settings";
import {
	EditOutlined,
	EyeOutlined,
	EyeInvisibleOutlined,
	FileSearchOutlined,
	UserOutlined,
	LinkOutlined,
	FileTextOutlined,
	FormatPainterOutlined
} from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "../../context/organizationContext";
import { useSelector } from "react-redux";
import AppNavigation from "../../components/AppNavigation";
import {ImeanHeader} from "../../components/Header/Header";
import {StickyNavigationHeader} from "../../components/Navigation/StickyNavigationHeader"

const { TextArea } = Input;
const { Option } = Select;
const baseUrl = process.env.REACT_APP_API_URL;

const AlertsManagementPage = () => {
	const { t } = useTranslation();
	const {organization} = useOrganization();
	const navigate = useNavigate();
	const user = useSelector((obj) => obj.auth.user);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [isViewOnly, setIsViewOnly] = useState(false);
	const [currentNotification, setCurrentNotification] = useState(null);
	const [notifications, setNotifications] = useState([]);
	const [generatedPost, setGeneratedPost] = useState("");
	const [loading, setLoading] = useState(false);
	const [selectedEmployees, setSelectedEmployees] = useState([]);
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 15,
		count: 0
	});
	const [form] = Form.useForm();
	const [submitLoading, setSubmitLoading] = useState(false);
	const [alertType, setAlertType] = useState(undefined);
	const [isFormatModalVisible, setIsFormatModalVisible] = useState(false);
	const [postModel, setPostModel] = useState(SETTINGS.POST_MODEL.DEFAULT);
	const [postLength, setPostLength] = useState(SETTINGS.POST_LENGTH.DEFAULT);
	const [postEmojis, setPostEmojis] = useState(SETTINGS.POST_EMOJIS.DEFAULT);
	const [selectedLanguages, setSelectedLanguages] = useState(['ru']); // Set only Russian by default
	const [selectedSocialNetwork, setSelectedSocialNetwork] = useState('LinkedIn');
	const [formattingLoading, setFormattingLoading] = useState(false);

    // useEffect(() => {
    // 	console.log("Organization context:", organization);
    // 	console.log("Employees available:", organization?.employees);
    // }, [organization]);

    const employeesList = useMemo(() => {
        if (!organization?.employees || !Array.isArray(organization.employees)) {
            // console.log("No employees data available or invalid format");
            return [];
        }
        // console.log("Processing employees for dropdown:", organization.employees);
        return organization.employees.map((emp) => ({
            key: emp.id,
            value: emp.id,
            label: emp.fullName || `Employee ${emp.id}`,
        }));
    }, [organization?.employees]);

    const getEmployeeName = (id) => {
        if (!organization?.employees) return id;
        const employee = organization.employees.find((emp) => emp.id === id);
        return employee ? employee.fullName || `Employee ${id}` : id;
    };

    const getEmployeeNames = (employeeIds) => {
        if (!employeeIds || !Array.isArray(employeeIds) || employeeIds.length === 0) return "-";
        return employeeIds.map((id) => getEmployeeName(id)).join(", ");
    };

    const isMobile = useMediaQuery({ maxWidth: 767 });
    const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });

	const fetchNotifications = () => {
		setLoading(true);

		fetchWrapper
			.get(`${baseUrl}/notifications/my?page=${pagination.page}&limit=${pagination.limit}`)
			.then((res) => {
				console.log("Полученные уведомления:", res.notifications);
				// Если createdAt отсутствует, добавляем текущую дату
				const notificationsWithDates = (res.notifications || []).map(notification => {
					if (!notification.createdAt) {
						return {...notification, createdAt: notification.updatedAt || new Date().toISOString()};
					}
					return notification;
				});
				setNotifications(notificationsWithDates);
				setPagination({
					...pagination,
					count: res.count || 0
				});
				setLoading(false);
			})
			.catch((err) => {
				console.error("Error fetching notifications:", err);
				message.error(t("alerts.fetchError"));
				setLoading(false);
				setNotifications([]);
			});
	};


	useEffect(() => {
		if (organization?.moderatorExist && user?.moderatorOrg){
			fetchNotifications();
		} else {
			navigate('/');
		}
	}, [pagination.page, pagination.limit]);

	const showCreateModal = () => {
		setIsEditing(false);
		setIsViewOnly(false);
		setCurrentNotification(null);
		form.resetFields();
		setSelectedEmployees([]);
		// Сбрасываем тип уведомления при создании нового
		setAlertType(undefined);
		setIsModalOpen(true);
	};

    const showEditModal = (notification) => {
        setIsEditing(true);
        setIsViewOnly(false);
        setCurrentNotification(notification);

		const employeeIds = notification.employees || [];
		form.setFieldsValue({
			title: notification.title,
			description: notification.description,
			type: notification.type,
			theme: notification.theme,
			prompt: notification.prompt || "",
			postLink: notification.postLink || "",
			postText: notification.postText || "",
			employees: employeeIds
		});

		// Устанавливаем сгенерированный текст поста, если он есть
		if (notification.generatedPost) {
			setGeneratedPost(notification.generatedPost);
		} else {
			setGeneratedPost("");
		}

		setSelectedEmployees(employeeIds);
		// Устанавливаем тип уведомления для отображения соответствующих полей
		if (notification.theme) {
			setAlertType(notification.theme);
		}
		console.log("Editing notification with employees:", employeeIds);
		setIsModalOpen(true);
	};

    const showViewModal = (notification) => {
        setIsEditing(false);
        setIsViewOnly(true);
        setCurrentNotification(notification);

		const employeeIds = notification.employees || [];
		form.setFieldsValue({
			title: notification.title,
			description: notification.description,
			type: notification.type,
			theme: notification.theme,
			prompt: notification.prompt || "",
			postLink: notification.postLink || "",
			postText: notification.postText || "",
			employees: employeeIds
		});
		setSelectedEmployees(employeeIds);
		// Устанавливаем тип уведомления для отображения соответствующих полей
		if (notification.theme) {
			setAlertType(notification.theme);
		}
		console.log("Viewing notification with employees:", employeeIds);
		setIsModalOpen(true);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		form.resetFields();
		setSubmitLoading(false);
	};

	const handleFormatButtonClick = () => {
		setIsFormatModalVisible(true);
	};

	const handleFormatCancel = () => {
		setIsFormatModalVisible(false);
	};

	const handleFormatSubmit = useCallback(() => {
		if (!selectedLanguages || selectedLanguages.length === 0) {
			notification.warning({
				message: t("common.warning"),
				description: t("posts.selectAtLeastOneLanguage")
			});
			return;
		}

		// Просто сохраняем настройки форматирования и закрываем модальное окно
		console.log("Saving format settings:", {
			model: postModel,
			length: postLength,
			emoji: postEmojis,
			languages: selectedLanguages,
			selectedContentLangs: getLanguageNames(selectedLanguages)
		});

		setIsFormatModalVisible(false);
		notification.success({
			message: t("common.success"),
			description: t("posts.settingsSaved")
		});
	}, [postModel, postLength, postEmojis, selectedLanguages, t]);

	const handleSubmit = async () => {
		try {
			await form.validateFields();
			const values = form.getFieldsValue();

			setSubmitLoading(true);

			try {
				if (isEditing) {
					// Подготавливаем запрос с учетом параметров форматирования
					const requestData = {
						title: values.title,
						description: values.description,
						type: values.type,
						theme: values.theme,
						prompt: values.prompt,
						postLink: values.postLink,
						postText: values.postText,
						employees: values.employees || [],
						formatId: null,
						postModel: postModel,
						postLength: postLength,
						postEmojis: postEmojis,
						selectedContentLangs: getLanguageNames(selectedLanguages),
						socialNetwork: currentNotification.socialNetwork || selectedSocialNetwork || "LinkedIn",
						date: currentNotification.date || new Date().toISOString()
					};

					console.log("Updating notification with format settings:", requestData);

					const response = await fetchWrapper.patch(
						`${baseUrl}/notifications/${currentNotification.id}`,
						requestData
					);

					if (response.success) {
						notification.success({
							message: t("alerts.success"),
							description: t("alerts.editSuccess")
						});

						// Проверяем, были ли созданы новые уведомления для добавленных сотрудников
						if (response.newNotificationsCreated && response.newNotificationsCreated.length > 0) {
							notification.info({
								message: t("alerts.newNotificationsCreated"),
								description: t("alerts.newNotificationsCreatedDesc", {
									count: response.newNotificationsCreated.length
								}),
								duration: 5
							});
						}

						handleCancel();
						fetchNotifications();
					} else {
						notification.error({
							message: t("alerts.error"),
							description: t("alerts.editError")
						});
					}
				} else {
					// Подготавливаем запрос с учетом параметров форматирования
					const requestData = {
						title: values.title,
						description: values.description,
						type: values.type,
						theme: values.theme,
						prompt: values.prompt,
						postLink: values.postLink,
						postText: values.postText,
						employees: values.employees || [],
						formatId: null,
						postModel: postModel,
						postLength: postLength,
						postEmojis: postEmojis,
						selectedContentLangs: getLanguageNames(selectedLanguages),
						socialNetwork: selectedSocialNetwork || "LinkedIn",
						date: new Date().toISOString()
					};

					console.log("Sending notification with format settings:", requestData);

					const response = await fetchWrapper.post(
						`${baseUrl}/notifications`,
						requestData
					);

					if (response.success) {
						// Проверяем тип уведомления
						if (values.theme === "create_post") {
							// Для create_post показываем список пользователей, для которых созданы посты
							if (response.successfulEmployees && response.successfulEmployees.length > 0) {
								// Показываем отдельное уведомление для каждого сотрудника
								response.successfulEmployees.forEach(emp => {
									notification.success({
										message: t("alerts.postCreatedSuccess"),
										description: t("alerts.postCreatedForUsers", {
											employeeName: emp.name,
											socialNetwork: emp.socialNetworks.join(', ')
										}),
										duration: 8
									});
								});
							} else {
								// Если нет успешных пользователей, но запрос успешный
								notification.success({
									message: t("alerts.success"),
									description: t("alerts.createSuccess")
								});
							}
						} else {
							// Для обычных уведомлений показываем стандартное сообщение
							notification.success({
								message: t("alerts.success"),
								description: t("alerts.createSuccess")
							});
						}

						// Показываем предупреждения для неудачных попыток создания постов
						if (Array.isArray(response.failedEmployees) && response.failedEmployees.length > 0) {
							response.failedEmployees.forEach(emp => {
								const employeeName = getEmployeeName(emp.id);
								notification.warning({
									message: t(`notifications.postCreationError`),
									description: t(`notifications.employeeNoSocials`, { employeeName: employeeName }),
									duration: 6
								});
							});
						}
						handleCancel();
						fetchNotifications();
					} else {
						notification.error({
							message: t("alerts.error"),
							description: t("alerts.createError")
						});
					}
				}
			} catch (error) {
				console.error("API error:", error);
				notification.error({
					message: t("alerts.error"),
					description: isEditing ? t("alerts.editError") : t("alerts.createError")
				});
			} finally {
				setSubmitLoading(false);
			}
		} catch (validationError) {
			console.error("Validation error:", validationError);
		}
	};

	const handleToggleVisibility = (notificationId, currentVisible) => {
		fetchWrapper
			.patch(`${baseUrl}/notifications/${notificationId}`, { visible: !currentVisible })
			.then((res) => {
				if (res.success) {
					notification.success({
						message: t("alerts.success"),
						description: currentVisible
							? t("alerts.hiddenSuccess")
							: t("alerts.visibleSuccess")
					});

					// После успешного обновления видимости, делаем запрос для получения актуального списка уведомлений
					fetchNotifications();
				}
			})
			.catch((err) => {
				console.error("Error toggling visibility:", err);
				notification.error({
					message: t("alerts.error"),
					description: t("alerts.visibilityError")
				});
			});
	};

	const baseColumns = [
		{
			title: t("alerts.title"),
			dataIndex: "title",
			key: "title",
			render: (text) => <div style={{ maxWidth: "150px", wordWrap: "break-word" }}>{text}</div>
		},
		{
			title: t("alerts.createdAt"),
			dataIndex: "createdAt",
			key: "createdAt",
			render: (date) => (
				<span>
					{date ? new Date(date).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
				</span>
			)
		},
		{
			title: t("alerts.theme"),
			dataIndex: "theme",
			key: "theme",
			render: (theme) => {
				const themeColors = {
					alert: "bg-purple-100 text-purple-800",
					suggestion: "bg-green-100 text-green-800"
				};
				return (
					<span className={`px-2 py-1 rounded-md ${themeColors[theme] || "bg-gray-100"}`}>
						{theme}
					</span>
				);
			}
		},
		{
			title: t("alerts.employees"),
			dataIndex: "employees",
			key: "employees",
			render: (employees) => (
				<div style={{ maxWidth: "200px", wordWrap: "break-word" }}>
					{employees && employees.length > 0 ? (
						<Tooltip title={getEmployeeNames(employees)}>
							<div className="truncate">
								{getEmployeeNames(employees)}
							</div>
						</Tooltip>
					) : (
						"-"
					)}
				</div>
			)
		},
		{
			title: t("alerts.actions"),
			key: "actions",
			render: (_, record) => (
				<div className="flex space-x-1">
					{record.visible ? (
						<>
							<Button
								type="text"
								icon={<EditOutlined />}
								onClick={() => showEditModal(record)}
							/>
							<Popconfirm
								title={t("alerts.hideConfirmTitle")}
								description={t("alerts.hideConfirmDescription")}
								onConfirm={() => handleToggleVisibility(record.id, true)}
								okText={t("alerts.yes")}
								cancelText={t("alerts.no")}
							>
								<Button
									type="text"
									icon={<EyeOutlined />}
								/>
							</Popconfirm>
						</>
					) : (
						<>
							<Tooltip title={t("alerts.viewNotificationTooltip")}>
								<Button
									type="text"
									icon={<FileSearchOutlined />}
									onClick={() => showViewModal(record)}
								/>
							</Tooltip>
							<Tooltip title={t("alerts.notVisibleTooltip")}>
								<Button
									type="text"
									icon={<EyeInvisibleOutlined />}
									disabled
								/>
							</Tooltip>
						</>
					)}
				</div>
			)
		}
	];

	const mobileColumns = [
		{
			title: t("alerts.title"),
			dataIndex: "title",
			key: "title",
			render: (text) => <div style={{ maxWidth: "150px", wordWrap: "break-word" }}>{text}</div>
		},
		{
			title: t("alerts.createdAt"),
			dataIndex: "createdAt",
			key: "createdAt",
			render: (date) => (
				<span>
					{date ? new Date(date).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
				</span>
			)
		},
		{
			title: t("alerts.actions"),
			key: "actions",
			render: (_, record) => (
				<div className="flex space-x-1">
					{record.visible ? (
						<>
							<Button
								type="text"
								icon={<EditOutlined />}
								onClick={() => showEditModal(record)}
							/>
							<Popconfirm
								title={t("alerts.hideConfirmTitle")}
								description={t("alerts.hideConfirmDescription")}
								onConfirm={() => handleToggleVisibility(record.id, true)}
								okText={t("alerts.yes")}
								cancelText={t("alerts.no")}
							>
								<Button
									type="text"
									icon={<EyeOutlined />}
								/>
							</Popconfirm>
						</>
					) : (
						<>
							<Tooltip title={t("alerts.viewNotificationTooltip")}>
								<Button
									type="text"
									icon={<FileSearchOutlined />}
									onClick={() => showViewModal(record)}
								/>
							</Tooltip>
							<Tooltip title={t("alerts.notVisibleTooltip")}>
								<Button
									type="text"
									icon={<EyeInvisibleOutlined />}
									disabled
								/>
							</Tooltip>
						</>
					)}
				</div>
			)
		}
	];

	const tabletColumns = [
		{
			title: t("alerts.title"),
			dataIndex: "title",
			key: "title",
			render: (text) => <div style={{ maxWidth: "150px", wordWrap: "break-word" }}>{text}</div>
		},
		{
			title: t("alerts.description"),
			dataIndex: "description",
			key: "description",
			render: (text) => (
				<Tooltip title={text}>
					<span className="truncate block max-w-xs">{text}</span>
				</Tooltip>
			)
		},
		{
			title: t("alerts.createdAt"),
			dataIndex: "createdAt",
			key: "createdAt",
			render: (date) => (
				<span>
					{date ? new Date(date).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
				</span>
			)
		},
		{
			title: t("alerts.theme"),
			dataIndex: "theme",
			key: "theme",
			render: (theme) => {
				const themeColors = {
					alert: "bg-purple-100 text-purple-800",
					suggestion: "bg-green-100 text-green-800"
				};
				return (
					<span className={`px-2 py-1 rounded-md ${themeColors[theme] || "bg-gray-100"}`}>
						{theme}
					</span>
				);
			}
		},
		{
			title: t("alerts.employees"),
			dataIndex: "employees",
			key: "employees",
			render: (employees) => (
				<div style={{ maxWidth: "200px", wordWrap: "break-word" }}>
					{employees && employees.length > 0 ? (
						<Tooltip title={getEmployeeNames(employees)}>
							<div className="truncate">
								{getEmployeeNames(employees)}
							</div>
						</Tooltip>
					) : (
						"-"
					)}
				</div>
			)
		},
		{
			title: t("alerts.actions"),
			key: "actions",
			render: (_, record) => (
				<div className="flex space-x-1">
					{record.visible ? (
						<>
							<Button
								type="text"
								icon={<EditOutlined />}
								onClick={() => showEditModal(record)}
							/>
							<Popconfirm
								title={t("alerts.hideConfirmTitle")}
								description={t("alerts.hideConfirmDescription")}
								onConfirm={() => handleToggleVisibility(record.id, true)}
								okText={t("alerts.yes")}
								cancelText={t("alerts.no")}
							>
								<Button
									type="text"
									icon={<EyeOutlined />}
								/>
							</Popconfirm>
						</>
					) : (
						<>
							<Tooltip title={t("alerts.viewNotificationTooltip")}>
								<Button
									type="text"
									icon={<FileSearchOutlined />}
									onClick={() => showViewModal(record)}
								/>
							</Tooltip>
							<Tooltip title={t("alerts.notVisibleTooltip")}>
								<Button
									type="text"
									icon={<EyeInvisibleOutlined />}
									disabled
								/>
							</Tooltip>
						</>
					)}
				</div>
			)
		}
	];

    const columns = useMemo(() => {
        return isMobile ? mobileColumns : isTablet ? tabletColumns : baseColumns;
    }, [isMobile, isTablet]);

    const handlePageChange = (page, pageSize) => {
        setPagination({
            ...pagination,
            page,
            limit: pageSize,
        });
    };

    const handleSelectAllEmployees = () => {
        if (!organization?.employees || !Array.isArray(organization.employees)) {
            console.log("Cannot select all: No employees available");
            return;
        }

        const allEmployeeIds = organization.employees.map((emp) => emp.id);
        // console.log("Selecting all employees:", allEmployeeIds);
        setSelectedEmployees(allEmployeeIds);
        form.setFieldsValue({ employees: allEmployeeIds });
    };

	const handleClearEmployees = () => {
		console.log("Clearing all selected employees");
		setSelectedEmployees([]);
		form.setFieldsValue({ employees: [] });
	};

	const getLanguageNames = useCallback((langCodes) => {
		return langCodes.map(code => {
			switch(code) {
				case 'ru': return 'Russian';
				case 'en': return 'English';
				case 'kk': return 'Kazakh';
				default: return code;
			}
		});
	}, []);

	return (
		<div className="alerts-management-page min-h-screen bg-gradient-to-b from-white to-violet-50 pb-24">
			{/* Header */}
			<ImeanHeader initialDark={true}></ImeanHeader>
            <StickyNavigationHeader headerTitle={t('home.alertsManagement')} onBackClick={() => navigate('/home')}></StickyNavigationHeader>

			{/* Content */}
			<div className="max-w-6xl mx-auto w-full px-3 md:px-6">
				<div className="mb-6 flex justify-center items-center">
					<Button
						type="primary"
						onClick={showCreateModal}
						className="!rounded-lg bg-primary hover:bg-primary-600 text-white my-4"
					>
                        {t("alerts.createNotification")}
					</Button>
				</div>

				<Card className="shadow-sm rounded-2xl">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
						<Typography.Title level={4} style={{ margin: 0 }}>
                            {t("alerts.history")}
						</Typography.Title>
						<Typography.Text type="secondary">
                            {t("alerts.hintCreateAlerts")}
						</Typography.Text>
					</div>

					<Divider />

					<div className="overflow-x-auto w-full">
						<Table
							dataSource={notifications}
							// Make column titles safe (no i18n objects)
							columns={(columns || []).map((col) => ({
								...col,
								title:
									typeof col.title === "string"
										? col.title
										: col.key === "actions"
											? "Actions"
											: col.dataIndex === "createdAt"
												? "Created"
												: col.dataIndex === "description"
													? "Description"
													: col.dataIndex === "employees"
														? "Employees"
														: col.dataIndex === "theme"
															? "Theme"
															: col.dataIndex === "title"
																? "Title"
																: "Column",
							}))}
							pagination={false}
							loading={loading}
							locale={{ emptyText: "No notifications" }}
							rowKey="id"
							expandable={
								isMobile
									? {
										expandedRowRender: (record) => (
											<div className="p-4">
												<p>
													<strong>Description:</strong> {record.description || "-"}
												</p>
												<p>
													<strong>Created:</strong>{" "}
													{record.createdAt
														? new Date(record.createdAt).toLocaleString("ru-RU", {
															day: "2-digit",
															month: "2-digit",
															year: "numeric",
															hour: "2-digit",
															minute: "2-digit",
														})
														: "-"}
												</p>
												<p>
													<strong>Theme:</strong> {record.theme || "-"}
												</p>
												<p>
													<strong>Employees:</strong> {getEmployeeNames(record.employees)}
												</p>
											</div>
										),
									}
									: undefined
							}
						/>
					</div>

					<div className={`flex ${isMobile ? "justify-center" : "justify-end"} mt-4`}>
						<Pagination
							current={pagination.page}
							pageSize={pagination.limit}
							total={pagination.count}
							onChange={handlePageChange}
							showSizeChanger={!isMobile}
							pageSizeOptions={["15", "30", "50"]}
							size={isMobile ? "small" : "default"}
						/>
					</div>
				</Card>
			</div>

			{/* Create / Edit / View modal */}
			<Modal
				title={isViewOnly ? t("alerts.viewNotification") : isEditing ? t("alerts.editNotification") : t("alerts.createNotification")}
				open={isModalOpen}
				onOk={handleSubmit}
				onCancel={handleCancel}
				okText={isEditing ? t("alerts.update") : t("alerts.create")}
				cancelText={t("alerts.cancel")}
				width={isMobile ? "95%" : "520px"}
				centered
				footer={
					isViewOnly
						? null
						: [
							<Button key="back" onClick={handleCancel}>
                                {t("alerts.cancel")}
							</Button>,
							<Button key="submit" type="primary" loading={submitLoading} onClick={handleSubmit}>
								{isEditing ? t("alerts.update") : t("alerts.create")}
							</Button>,
						]
				}
			>
				<Form form={form} layout="vertical" disabled={isViewOnly}>
					<Form.Item
						name="title"
						label={t("alerts.title")}
						rules={[{ required: true, min: 2, max: 200, message: t("alerts.hint.title") }]}
					>
						<Input placeholder={t("alerts.placeholder.title")} />
					</Form.Item>

					<Form.Item
						name="description"
						label={t("alerts.description")}
						rules={[{ required: true, min: 2, max: 2000, message: t("alerts.hint.description") }]}
					>
						<TextArea
							rootClassName="!resize-none"
							rows={4}
							placeholder={t("alerts.placeholder.description")}
							maxLength={2000}
						/>
					</Form.Item>

					{/* Hidden Type field (kept intact) */}
					<Form.Item
						name="type"
						label="Type"
						rules={[{ required: true, message: "Please select a type" }]}
						style={{ display: "none" }}
						initialValue="info"
					>
						<Select placeholder="Select type">
							<Option value="info">Info</Option>
							<Option value="warning">Warning</Option>
							<Option value="danger">Danger</Option>
						</Select>
					</Form.Item>

					<Form.Item name="theme" label={t("alerts.theme")} rules={[{ required: true, message: t("alerts.hint.theme") }]}>
						{isEditing ? (
							<div className="p-2 border border-gray-200 rounded">
								{alertType === "alert" && t("alerts.themeOption.alert")}
								{alertType === "suggestion" && t("alerts.themeOption.suggestion")}
								{alertType === "create_post" && t("alerts.themeOption.createPost")}
							</div>
						) : (
							<Select placeholder={t("alerts.placeholder.theme")} onChange={(value) => setAlertType(value)}>
								<Option value="alert">{t("alerts.themeOption.alert")}</Option>
								<Option value="suggestion">{t("alerts.themeOption.suggestion")}</Option>
								<Option value="create_post">{t("alerts.themeOption.createPost")}</Option>
							</Select>
						)}
					</Form.Item>

					{alertType === "create_post" && isEditing && (
						<>
							{currentNotification?.postLink && (
								<div className="mb-4">
									<div className="font-medium mb-2">{t("alerts.cardPost.linkTitle")}</div>
									<div className="p-3 bg-gray-50 border border-gray-200 rounded overflow-auto break-all">
										<a href={currentNotification.postLink} target="_blank" rel="noopener noreferrer">
											{currentNotification.postLink}
										</a>
									</div>
								</div>
							)}

							{currentNotification?.postText && (
								<div className="mb-4">
									<div className="font-medium mb-2">{t("alerts.cardPost.sourceTextTitle")}</div>
									<div className="p-3 bg-gray-50 border border-gray-200 rounded overflow-auto whitespace-pre-wrap">
										{currentNotification.postText}
									</div>
								</div>
							)}

							{generatedPost && (
								<div className="mb-4">
									<div className="font-medium mb-2">{t("alerts.cardPost.generatePost")}</div>
									<div className="p-3 bg-gray-50 border border-gray-200 rounded overflow-auto whitespace-pre-wrap">
										{generatedPost}
									</div>
								</div>
							)}
						</>
					)}

					{alertType === "create_post" && !isEditing && (
						<>
							<Form.Item name="prompt" label={t("alerts.cardPost.labelPrompt")} rules={[{ required: false }]}>
								<Input placeholder={t("alerts.placeholder.prompt")} />
							</Form.Item>

							<Tabs
								defaultActiveKey={form.getFieldValue("postText") ? t("alerts.text") : t("alerts.link")}
								items={[
									{
										key: "link",
										label: t("alerts.link"),
										icon: <LinkOutlined />,
										children: (
											<Form.Item name="postLink" label={t("alerts.cardPost.linkTitle")} rules={[{ required: false }]}>
												<Input placeholder={t("alerts.placeholder.link")} />
											</Form.Item>
										),
									},
									{
										key: "text",
										label: t("alerts.text"),
										icon: <FileTextOutlined />,
										children: (
											<Form.Item name="postText" label={t("alerts.cardPost.sourceTextTitle")} rules={[{ required: false }]}>
												<TextArea
													rootClassName="!resize-none"
													rows={4}
													placeholder={t("alerts.placeholder.sourceText")}
													maxLength={2000}
												/>
											</Form.Item>
										),
									},
								]}
							/>

							<div className="mb-4">
								<Button type="primary" onClick={handleFormatButtonClick} className="!rounded-lg bg-primary hover:bg-primary-600">
                                    {t("alerts.buttons.formattingOptions")}
								</Button>
							</div>
						</>
					)}

					<Form.Item
						name="employees"
						label={t("alerts.employees")}
						rules={[{ required: true, message: t("alerts.hint.employees") }]}
					>
						<div className="flex flex-col space-y-2">
							<Select
								mode="multiple"
								placeholder={t("alerts.placeholder.employees")}
								style={{ width: "100%" }}
								onChange={(values) => {
									setSelectedEmployees(values);
									form.setFieldsValue({ employees: values });
								}}
								value={selectedEmployees}
							>
								{(organization?.employees || []).map((employee) => (
									<Select.Option key={employee.id} value={employee.id}>
										{employee.fullName || `Employee ${employee.id}`}
									</Select.Option>
								))}
							</Select>
							<Space>
								<Button size="small" type="default" onClick={handleSelectAllEmployees} icon={<UserOutlined />}>
                                    {t("alerts.buttons.selectAll")}
								</Button>
								<Button size="small" type="default" onClick={handleClearEmployees}>
                                    {t("alerts.buttons.clear")}
								</Button>
							</Space>
						</div>
					</Form.Item>
				</Form>
			</Modal>

			{/* Formatting modal */}
			<Modal
				title={t("alerts.cardFormat.title")}
				open={isFormatModalVisible}
				onOk={handleFormatSubmit}
				onCancel={handleFormatCancel}
				confirmLoading={formattingLoading}
				className="format-modal"
				width={450}
			>
				<div className="format-settings">
					<div className="w-full mb-4">
						<div className="mb-2 font-medium">{t("alerts.cardFormat.language")}</div>
						<Select
							mode="multiple"
							placeholder={t("alerts.placeholder.language")}
							value={selectedLanguages}
							onChange={(values) => setSelectedLanguages(values)}
							style={{ width: "100%" }}
							options={[
								{ value: "ru", label: "Русский" },
								{ value: "en", label: "English" },
								{ value: "kk", label: "Қазақша" },
							]}
						/>
					</div>

					<div className="w-full mb-4">
						<div className="mb-2 font-medium">{t("alerts.cardFormat.model")}</div>
						<Select
							value={postModel}
							onChange={(value) => setPostModel(value)}
							style={{ width: "100%" }}
							options={[
								{ value: "basic", label: "Basic" },
								{ value: "advance", label: "Advanced" },
							]}
						/>
					</div>

					<div className="w-full mb-4">
						<div className="mb-2 font-medium">{t("alerts.cardFormat.length")}</div>
						<div className="flex items-center">
							<div className="flex-1">
								<Slider
									min={SETTINGS.POST_LENGTH.MIN}
									max={SETTINGS.POST_LENGTH.MAX}
									step={SETTINGS.POST_LENGTH.STEP}
									value={postLength}
									onChange={setPostLength}
								/>
							</div>
							<div className="ml-4">
								<InputNumber
									min={SETTINGS.POST_LENGTH.MIN}
									max={SETTINGS.POST_LENGTH.MAX}
									step={SETTINGS.POST_LENGTH.STEP}
									value={postLength}
									onChange={setPostLength}
									style={{ width: "80px" }}
								/>
							</div>
						</div>
					</div>

					<div className="w-full mb-2">
						<div className="mb-2 font-medium">{t("alerts.cardFormat.emojis")}</div>
						<Radio.Group
							options={[
								{ label: t("alerts.yes"), value: true },
								{ label: t("alerts.no"), value: false },
							]}
							onChange={(e) => setPostEmojis(e.target.value)}
							value={postEmojis}
							optionType="button"
							buttonStyle="solid"
						/>
					</div>
				</div>
			</Modal>

			<AppNavigation />
		</div>
	);
};

export default AlertsManagementPage;
